/**
 * E2E Verschluesselung fuer Online-Anamnese
 * AES-256-GCM + RSA-OAEP mit AAD (Additional Authenticated Data)
 *
 * AAD = patient_id:token:key_version
 * Verhindert Payload-Tausch zwischen Patienten
 *
 * Web Crypto API (browser-native, keine Dependencies)
 */

function pemToArrayBuffer(pem) {
  const b64 = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');
  const binaryString = window.atob(b64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

async function importRsaPublicKey(pemKey) {
  const keyData = pemToArrayBuffer(pemKey);
  return window.crypto.subtle.importKey(
    'spki',
    keyData,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    true,
    ['encrypt']
  );
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Verschluesselt Anamnese-Daten mit AES-256-GCM + RSA-OAEP + AAD
 *
 * @param {Object} data - Die zu verschluesselnden Daten
 * @param {string} publicKeyPem - RSA Public Key im PEM-Format
 * @param {string} patientId - Patienten-ID fuer AAD
 * @param {string} token - Anamnese-Token fuer AAD
 * @param {number} keyVersion - Key-Version fuer AAD
 * @returns {Object} { encrypted_data, encrypted_key, iv, key_version, algorithm }
 */
export async function encryptAnamneseData(data, publicKeyPem, patientId, token, keyVersion) {
  try {
    // 1. AES-256-GCM Schluessel generieren
    const aesKey = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt']
    );

    // 2. AAD zusammenbauen (verhindert Payload-Tausch zwischen Patienten)
    const aad = new TextEncoder().encode(`${patientId}:${token}:${keyVersion}`);

    // 3. Daten verschluesseln mit AES-GCM + AAD
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    const ciphertext = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        additionalData: aad,
        tagLength: 128
      },
      aesKey,
      plaintext
    );

    // 4. AES-Key mit RSA-OAEP verschluesseln
    const rsaKey = await importRsaPublicKey(publicKeyPem);
    const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);
    const encryptedKey = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      rsaKey,
      rawAesKey
    );

    // 5. Alles Base64-kodiert zurueckgeben
    return {
      encrypted_data: arrayBufferToBase64(ciphertext),
      encrypted_key: arrayBufferToBase64(encryptedKey),
      iv: arrayBufferToBase64(iv),
      key_version: keyVersion,
      algorithm: 'AES-256-GCM+RSA-OAEP-SHA256'
    };

  } catch (error) {
    console.error('Verschluesselungsfehler:', error);
    throw new Error('Daten konnten nicht verschluesselt werden. Bitte verwenden Sie einen aktuellen Browser.');
  }
}

/**
 * Prueft ob Web Crypto API verfuegbar ist
 */
export function isEncryptionSupported() {
  return !!(window.crypto && window.crypto.subtle);
}
